// Source de vérité unique pour le Vision Health Report 2026.
// Données réelles issues du réseau optique Lapaire (2019-2026).
// Le rapport ne publie que des indicateurs de santé visuelle :
// aucune donnée commerciale (achats, ventes, taux de conversion) n'y figure.

export interface CountryStats {
  country: string;
  slug: string;
  patients: number;
  myopia: number;
  hyperopia: number;
  astigmatism: number;
  presbyopia: number;
  severity_score: number;
  /** Patients whose eye test identified a need for glasses. Supplied by the data team. */
  needs_correction_n?: number;
}

export interface AgeBucket {
  age_group: string;
  patients: number;
}

export interface SeverityBucket {
  bucket: string;
  patients: number;
}

export interface TerritoryStats {
  territory: string;
  patients: number;
  severity_score: number;
}

export interface KeyFinding {
  country: string;
  value: number;
}

// Slugs alignés sur src/config/countries.ts (URL pays).
const SLUGS: Record<string, string> = {
  "Cote d'Ivoire": "cote-divoire",
  Togo: "togo",
  "Burkina Faso": "burkina-faso",
  Benin: "benin",
  Mali: "mali",
  Senegal: "senegal",
  Maroc: "maroc",
  "DR Congo": "rdc",
};

const RAW_COUNTRIES: Omit<CountryStats, "slug">[] = [
  { country: "Cote d'Ivoire", patients: 336349, myopia: 25.12, hyperopia: 13.16, astigmatism: 9.72, presbyopia: 21.65, severity_score: 0.81, needs_correction_n: 164604 },
  { country: "Togo", patients: 252027, myopia: 16.70, hyperopia: 10.85, astigmatism: 10.92, presbyopia: 22.06, severity_score: 0.75, needs_correction_n: 101375 },
  { country: "Burkina Faso", patients: 187303, myopia: 23.92, hyperopia: 11.51, astigmatism: 11.27, presbyopia: 29.78, severity_score: 0.88, needs_correction_n: 97537 },
  { country: "Benin", patients: 177335, myopia: 21.82, hyperopia: 12.78, astigmatism: 10.88, presbyopia: 21.04, severity_score: 0.84, needs_correction_n: 81815 },
  { country: "Mali", patients: 153619, myopia: 15.99, hyperopia: 10.13, astigmatism: 8.82, presbyopia: 23.82, severity_score: 0.75, needs_correction_n: 61720 },
  { country: "Senegal", patients: 29818, myopia: 26.70, hyperopia: 30.57, astigmatism: 36.88, presbyopia: 40.94, severity_score: 1.68, needs_correction_n: 21951 },
  { country: "Maroc", patients: 11711, myopia: 28.61, hyperopia: 26.45, astigmatism: 25.39, presbyopia: 19.14, severity_score: 1.47, needs_correction_n: 7441 },
  { country: "DR Congo", patients: 3173, myopia: 19.64, hyperopia: 27.93, astigmatism: 28.31, presbyopia: 34.60, severity_score: 1.27, needs_correction_n: 698 },
];

for (const country of RAW_COUNTRIES) {
  const need = country.needs_correction_n;
  if (need == null || need > country.patients) {
    throw new Error(`Invalid correction-need counts for ${country.country}`);
  }
}

const TOTAL_CONFIRMED_NEED = RAW_COUNTRIES.reduce((sum, country) => sum + (country.needs_correction_n ?? 0), 0);

if (TOTAL_CONFIRMED_NEED !== 537141) {
  throw new Error("Vision Health correction-need totals do not match the validated extraction");
}

export const REPORT = {
  metadata: {
    report_year: 2026,
    patients: 1149296,
    countries: 8,
    territories: 120,
    period: "2019-2026",
    source: "Lapaire Optical Network",
  },
  countries: RAW_COUNTRIES.map((c) => ({
    ...c,
    slug: SLUGS[c.country] ?? c.country.toLowerCase().replace(/\s+/g, "-"),
  })) as CountryStats[],
  key_findings: {
    highest_myopia: { country: "Maroc", value: 28.61 } as KeyFinding,
    highest_hyperopia: { country: "Senegal", value: 30.57 } as KeyFinding,
    highest_astigmatism: { country: "Senegal", value: 36.88 } as KeyFinding,
    highest_presbyopia: { country: "Senegal", value: 40.94 } as KeyFinding,
  },
  age_distribution: [
    { age_group: "0-4", patients: 31220 },
    { age_group: "5-17", patients: 91829 },
    { age_group: "18-29", patients: 460566 },
    { age_group: "30-39", patients: 215680 },
    { age_group: "40-49", patients: 179714 },
    { age_group: "50-59", patients: 103999 },
    { age_group: "60+", patients: 65048 },
  ] as AgeBucket[],
  severity_buckets: [
    { bucket: "0-0.25", patients: 370771 },
    { bucket: "0.25-0.50", patients: 185357 },
    { bucket: "0.50-0.75", patients: 132302 },
    { bucket: "0.75-1.00", patients: 55599 },
    { bucket: "1.00-1.50", patients: 112580 },
    { bucket: "1.50-2.00", patients: 100831 },
    { bucket: "2+", patients: 191856 },
  ] as SeverityBucket[],
  top_territories: [
    { territory: "Angre-Abidjan-CI", patients: 11172, severity_score: 1.04 },
    { territory: "Cocody Danga-Abidjan-CI", patients: 20207, severity_score: 1.11 },
    { territory: "Bodo-CI", patients: 3582, severity_score: 1.36 },
    { territory: "9 Kilo-CI", patients: 15491, severity_score: 0.99 },
    { territory: "Koulouba-Ouaga-BF", patients: 31452, severity_score: 1.12 },
  ] as TerritoryStats[],
  gender: {
    summary: {
      total_with_valid_gender: 1133760,
      missing_or_unknown: 15536,
      distribution: [
        {
          gender: "Female",
          patients: 534991,
          share: 47.19,
          myopia: 23.89,
          hyperopia: 12.56,
          astigmatism: 10.34,
          presbyopia: 20.21,
          needs_correction: 47.80,
          avg_severity: 0.82,
        },
        {
          gender: "Male",
          patients: 598769,
          share: 52.81,
          myopia: 19.16,
          hyperopia: 11.47,
          astigmatism: 10.44,
          presbyopia: 26.36,
          needs_correction: 44.37,
          avg_severity: 0.84,
        },
      ] as GenderRow[],
      key_insights: [
        {
          title: "Men show higher presbyopia prevalence",
          value: "26.36% vs 20.21%",
          description: "Male patients show a higher presbyopia proxy rate in the dataset.",
        },
        {
          title: "Women show higher myopia prevalence",
          value: "23.89% vs 19.16%",
          description:
            "Female patients show a higher myopia rate across the analysed population.",
        },
      ],
    },
  },
} as const;

export interface GenderRow {
  gender: "Female" | "Male";
  patients: number;
  share: number;
  myopia: number;
  hyperopia: number;
  astigmatism: number;
  presbyopia: number;
  needs_correction: number;
  avg_severity: number;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

export function formatPct(n: number, digits = 1): string {
  return `${n.toFixed(digits)}%`;
}

/** Small samples are flagged as "interpret with caution". */
export const SMALL_SAMPLE_THRESHOLD = 15000;

/**
 * Share of tested people whose eye test identified a need for glasses.
 * Null until the real counts are supplied — we never publish an estimate.
 */
export function needShare(c: CountryStats): number | null {
  const need = c.needs_correction_n;
  if (need == null || c.patients <= 0) return null;
  return (need / c.patients) * 100;
}

export const REPORT_VERSION = "1.3";
export const REPORT_UPDATED = "2026-10-16";
